import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  CheckSquare,
  Layers,
  FileText,
  ClipboardList,
  Presentation,
  FileSearch,
  ArrowRight,
  X,
  Loader2,
  Mail,
  Download,
  FlaskConical,
  Atom,
  Leaf,
  Sigma,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RESOURCE } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  checklist: { label: "Checklist", icon: CheckSquare },
  flashcards: { label: "Flashcards", icon: Layers },
  specification: { label: "Spec Made Easy", icon: FileSearch },
  practice: { label: "Practice + Markscheme", icon: ClipboardList },
  powerpoint: { label: "Lesson PowerPoint", icon: Presentation },
  other: { label: "Resource", icon: FileText },
};

const CATEGORY_META = {
  chemistry: { label: "Chemistry", icon: FlaskConical },
  physics: { label: "Physics", icon: Atom },
  biology: { label: "Biology", icon: Leaf },
  maths: { label: "Maths", icon: Sigma },
  general: { label: "General", icon: FileText },
};

const LEVEL_LABEL = {
  KS3: "KS3",
  KS4: "KS4 · GCSE",
  KS5: "KS5 · A-Level",
  all: "All levels",
};

const formatPrice = (price, currency = "gbp") => {
  const symbol = currency === "gbp" ? "£" : currency.toUpperCase() + " ";
  return `${symbol}${Number(price).toFixed(2)}`;
};

export const ResourcesSection = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: "all",
    level: "all",
    type: "all",
  });
  const [selected, setSelected] = useState(null); // resource being purchased
  const [email, setEmail] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/resources`)
      .then((res) => setResources(res.data?.resources || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = resources.filter((r) => {
    if (filters.category !== "all" && r.category !== filters.category) return false;
    if (filters.level !== "all" && r.level !== filters.level && r.level !== "all") return false;
    if (filters.type !== "all" && r.resource_type !== filters.type) return false;
    return true;
  });

  const handleBuy = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email so we can send you the file.");
      return;
    }
    setRedirecting(true);
    try {
      const res = await axios.post(`${API}/resources/checkout`, {
        resource_id: selected.id,
        email,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout.");
        setRedirecting(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout.";
      toast.error(msg);
      setRedirecting(false);
    }
  };

  if (!loading && resources.length === 0) {
    return (
      <section id="resources" className="py-24 md:py-32 bg-[#fbfcfe]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
              Study materials
            </div>
            <h2
              data-testid={RESOURCE.sectionTitle}
              className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]"
            >
              Learning resources, ready to download.
            </h2>
            <p className="mt-5 text-base text-[#5c6b85] leading-relaxed">
              Checklists, flashcards, specification made easy, practice
              questions with full markscheme breakdowns and ready-to-use
              lesson PowerPoints. Hand-made by me, designed to slot straight
              into how your child is studying.
            </p>
          </div>

          <motion.div
            data-testid={RESOURCE.comingSoonBanner}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-14 rounded-3xl bg-[#f0f6fc] border border-[#dce6f0] p-10 md:p-14 relative overflow-hidden"
          >
            <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-[#ffe89a]/40 pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full bg-[#8ab8e8]/15 pointer-events-none" />

            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7">
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-medium px-3 py-1.5 rounded-full bg-[#ffe89a] text-[#2a3b5c]">
                  Coming soon
                </span>
                <h3 className="mt-5 font-heading text-2xl md:text-3xl font-semibold text-[#2a3b5c] leading-tight">
                  I'm putting the finishing touches on my first set of resources.
                </h3>
                <p className="mt-4 text-[#5c6b85] leading-relaxed">
                  Checklists, flashcards, spec-made-easy guides, practice
                  questions with full markscheme breakdowns, and lesson
                  PowerPoints, dropping here soon. Want to be first to know
                  when they go live?
                </p>
                <a
                  data-testid={RESOURCE.comingSoonContactLink}
                  href="#contact"
                  className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c] text-sm font-medium transition-colors"
                >
                  Drop me a message
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              <div className="md:col-span-5">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { Icon: CheckSquare, label: "Checklists" },
                    { Icon: Layers, label: "Flashcards" },
                    { Icon: FileSearch, label: "Spec made easy" },
                    { Icon: ClipboardList, label: "Practice + MS" },
                    { Icon: Presentation, label: "PowerPoints" },
                    { Icon: FileText, label: "Revision notes" },
                  ].map(({ Icon, label }) => (
                    <div
                      key={label}
                      className="rounded-2xl bg-white border border-[#dce6f0] p-4 flex items-center gap-3"
                    >
                      <span className="w-9 h-9 rounded-full bg-[#f0f6fc] flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#3d6fa8]" />
                      </span>
                      <span className="text-sm font-medium text-[#2a3b5c]">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="resources" className="py-24 md:py-32 bg-[#fbfcfe]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
              Study materials
            </div>
            <h2
              data-testid={RESOURCE.sectionTitle}
              className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2a3b5c]"
            >
              Learning resources, ready to download.
            </h2>
            <p className="mt-5 text-base text-[#5c6b85] leading-relaxed">
              Checklists, flashcards, specification made easy, practice
              questions with full markscheme breakdowns and ready-to-use
              lesson PowerPoints. Hand-made by me, designed to slot straight
              into how your child is studying.
            </p>
          </div>

          {resources.length > 0 && (
            <div className="grid grid-cols-3 gap-3 md:gap-4 md:max-w-md w-full">
              <Select
                value={filters.category}
                onValueChange={(v) => setFilters({ ...filters, category: v })}
              >
                <SelectTrigger
                  data-testid={RESOURCE.filterCategory}
                  className="rounded-2xl h-11 bg-[#f0f6fc] border-[#dce6f0] text-sm"
                >
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  <SelectItem value="chemistry">Chemistry</SelectItem>
                  <SelectItem value="physics">Physics</SelectItem>
                  <SelectItem value="biology">Biology</SelectItem>
                  <SelectItem value="maths">Maths</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.level}
                onValueChange={(v) => setFilters({ ...filters, level: v })}
              >
                <SelectTrigger
                  data-testid={RESOURCE.filterLevel}
                  className="rounded-2xl h-11 bg-[#f0f6fc] border-[#dce6f0] text-sm"
                >
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="KS3">KS3</SelectItem>
                  <SelectItem value="KS4">KS4</SelectItem>
                  <SelectItem value="KS5">KS5</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.type}
                onValueChange={(v) => setFilters({ ...filters, type: v })}
              >
                <SelectTrigger
                  data-testid={RESOURCE.filterType}
                  className="rounded-2xl h-11 bg-[#f0f6fc] border-[#dce6f0] text-sm"
                >
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="checklist">Checklist</SelectItem>
                  <SelectItem value="flashcards">Flashcards</SelectItem>
                  <SelectItem value="specification">Spec Made Easy</SelectItem>
                  <SelectItem value="practice">Practice + MS</SelectItem>
                  <SelectItem value="powerpoint">PowerPoint</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {loading && (
          <div className="mt-14 flex items-center gap-3 text-[#5c6b85]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading resources…
          </div>
        )}

        {!loading && filtered.length === 0 && resources.length > 0 && (
          <div className="mt-14 rounded-3xl bg-[#f0f6fc] border border-[#dce6f0] p-10 text-center text-[#5c6b85]">
            No resources match those filters yet.
          </div>
        )}

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((r, i) => {
            const typeMeta = TYPE_META[r.resource_type] || TYPE_META.other;
            const catMeta = CATEGORY_META[r.category] || CATEGORY_META.general;
            const TypeIcon = typeMeta.icon;
            const CatIcon = catMeta.icon;
            return (
              <motion.div
                key={r.id}
                data-testid={`${RESOURCE.card}-${r.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group bg-[#f0f6fc] border border-[#dce6f0] rounded-3xl p-7 flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-medium px-2.5 py-1 rounded-full bg-white text-[#3d6fa8] border border-[#dce6f0]">
                    <TypeIcon className="w-3.5 h-3.5" />
                    {typeMeta.label}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-medium px-2.5 py-1 rounded-full bg-[#ffe89a]/50 text-[#2a3b5c]">
                    <CatIcon className="w-3.5 h-3.5" />
                    {catMeta.label}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.15em] font-medium px-2.5 py-1 rounded-full bg-white text-[#5c6b85] border border-[#dce6f0]">
                    {LEVEL_LABEL[r.level] || r.level}
                  </span>
                </div>

                <h3 className="mt-5 font-heading text-xl font-semibold text-[#2a3b5c] leading-snug">
                  {r.title}
                </h3>
                {r.description && (
                  <p className="mt-3 text-sm text-[#5c6b85] leading-relaxed line-clamp-4">
                    {r.description}
                  </p>
                )}

                <div className="mt-auto pt-6 flex items-end justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.15em] text-[#5c6b85]">
                      Instant download
                    </div>
                    <div className="font-heading text-2xl font-semibold text-[#2a3b5c]">
                      {formatPrice(r.price, r.currency)}
                    </div>
                  </div>
                  <Button
                    data-testid={`${RESOURCE.buyButton}-${r.id}`}
                    onClick={() => {
                      setSelected(r);
                      setEmail("");
                    }}
                    className="rounded-full px-5 py-5 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                  >
                    Get it
                    <ArrowRight className="ml-1.5 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Purchase modal */}
      {selected && (
        <div
          data-testid={RESOURCE.modal}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#2a3b5c]/40 backdrop-blur-sm px-4"
          onClick={() => !redirecting && setSelected(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white border border-[#dce6f0] rounded-3xl max-w-md w-full p-8 shadow-2xl"
          >
            <button
              data-testid={RESOURCE.modalClose}
              onClick={() => setSelected(null)}
              disabled={redirecting}
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#5c6b85] hover:bg-[#f0f6fc] transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
              {(TYPE_META[selected.resource_type] || TYPE_META.other).label}
              {" · "}
              {LEVEL_LABEL[selected.level] || selected.level}
            </div>
            <h3 className="mt-3 font-heading text-2xl font-semibold text-[#2a3b5c] leading-snug">
              {selected.title}
            </h3>
            {selected.description && (
              <p className="mt-3 text-sm text-[#5c6b85] leading-relaxed">
                {selected.description}
              </p>
            )}

            <div className="mt-6">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Email for receipt + download
              </Label>
              <Input
                data-testid={RESOURCE.modalEmailInput}
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-3 rounded-2xl h-12 bg-[#fbfcfe] border-[#dce6f0]"
              />
            </div>

            <div className="mt-6 rounded-2xl bg-[#f0f6fc] border border-[#dce6f0] p-4 flex items-center justify-between">
              <span className="text-sm text-[#5c6b85]">Total</span>
              <span className="font-heading text-2xl font-semibold text-[#2a3b5c]">
                {formatPrice(selected.price, selected.currency)}
              </span>
            </div>

            <Button
              data-testid={RESOURCE.modalPayButton}
              onClick={handleBuy}
              disabled={redirecting || !email}
              className="mt-6 w-full rounded-full py-6 bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
            >
              {redirecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Pay &amp; download
                </>
              )}
            </Button>
            <p className="mt-3 text-xs text-[#5c6b85] text-center">
              Secure payment via Stripe. Instant download on the next page.
            </p>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default ResourcesSection;
